import {AbstractControl, AsyncValidatorFn, ValidationErrors, ValidatorFn, Validators} from '@angular/forms';
import {Observable, of} from 'rxjs';
import {delay, mergeMap} from 'rxjs/operators';

export class AceValidators {
  static readonly INT_32_UPPER_BOUND = 2147483647;
  // Regex from angular email validator
  private static readonly EMAIL_REGEXP = /^(?=.{1,254}$)(?=.{1,64}@)[a-zA-Z0-9!#$%&'*+/=?^_`{|}~-]+(?:\.[a-zA-Z0-9!#$%&'*+/=?^_`{|}~-]+)*@[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?(?:\.[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?)*$/;
  private static readonly EMAIL_USERNAME_REGEX = /^[a-zA-Z0-9]+$/;
  private static readonly IP_ADDRESS_REGEX = /^(?:(?:2[0-4]\d|25[0-5]|1\d{2}|[1-9]?\d)\.){3}(?:2[0-4]\d|25[0-5]|1\d{2}|[1-9]?\d)$/;
  private static readonly DNS_HOSTNAME_REGEX  = /^(([a-zA-Z0-9]|[a-zA-Z0-9][a-zA-Z0-9\-]*[a-zA-Z0-9])\.)*([A-Za-z0-9]|[A-Za-z0-9][A-Za-z0-9\-]*[A-Za-z0-9])$/;
  private static readonly URL_PORT_PART_REGEX = /(:\d+)$/;
  private static readonly INT_64_UPPER_BOUND = 9223372036854775807;


  // create validator that performs validation of url
  static urlValidator(trimUrl = false, portCheck = false, httpsOnly = false): ValidatorFn {
    return (control: AbstractControl) => {
      if (control.value) {
        const INVALID_URL_MESSAGE = {messageKey: 'A valid URL is required'};
        const HTTP_PROTOCOL = 'http';
        const HTTPS_PROTOCOL = 'https';
        const PROTOCOL_SEPARATOR = '://';
        const url = trimUrl ? control.value.trim() : control.value;
        try {
          const validUrl: URL = new URL(url);
          // Validate HTTP/HTTPS protocol with proper scheme/protocol separator. This catches both invalid
          // protocols AND incomplete separators like 'http:google.com' which are allowed by the URL constructor
          if (httpsOnly) {
            if (!url.startsWith(HTTPS_PROTOCOL + PROTOCOL_SEPARATOR)) {
              return {invalidHttpsUrl: {messageKey: 'A valid HTTPS URL is required'}};
            }
          } else if (!url.startsWith(HTTP_PROTOCOL + PROTOCOL_SEPARATOR) && !url.startsWith(HTTPS_PROTOCOL + PROTOCOL_SEPARATOR)) {
            return {invalidUrl: INVALID_URL_MESSAGE};
          }
          if (portCheck) {
            const urlWithoutProtocol = url.split(PROTOCOL_SEPARATOR)[1];
            const pathStartIndex = urlWithoutProtocol.indexOf('/');
            const authDomainAndPortOnly = pathStartIndex > 0 ? urlWithoutProtocol.substring(0, pathStartIndex) : urlWithoutProtocol;
            const atIndex = authDomainAndPortOnly.indexOf('@');
            const domainAndPortOnly = atIndex > 0 ? authDomainAndPortOnly.substring(atIndex + 1) : authDomainAndPortOnly;
            const portOnly = domainAndPortOnly.match(this.URL_PORT_PART_REGEX);
            if (!portOnly) {
              return {missingPort: {messageKey: 'A port number is required'}};
            }
          }
        } catch (error: any) {
          if (error instanceof TypeError) {
            return {invalidUrl: INVALID_URL_MESSAGE};
          } else {
            throw error;
          }
        }
      }
      return null;
    };
  }

  static isValidDNSHostname(hostname: string): boolean {
    return this.DNS_HOSTNAME_REGEX.test(hostname);
  }

  static isValidIpAddress(ipAddress: string): boolean {
    return this.IP_ADDRESS_REGEX.test(ipAddress);
  }


  // create validator that performs validation of an IP address
  static ipAddressOrDNSHostnameValidator(getControlValueFn = (control: AbstractControl) => control.value): ValidatorFn {
    return (control: AbstractControl) => {
      const value = getControlValueFn(control);
      if (value != null && !this.isValidIpAddress(value) && !this.isValidDNSHostname(value)) {
        return {invalidIPOrDNSHostname: {messageKey: 'A valid IP address or DNS hostname is required'}};
      }
      return null;
    };
  }
  // create validator that performs validation of an IP address
  static ipAddressValidator(getControlValueFn = (control: AbstractControl) => control.value): ValidatorFn {
    return (control: AbstractControl) => {
      const value = getControlValueFn(control);
      if (value != null && value !== '' && !this.isValidIpAddress(value)) {
        return {invalidIP: {messageKey: 'A valid IP address is required'}};
      }
      return null;
    };
  }



  // create a validator that returns error if not all or none of the controls have a value
  static allOrNoneValidatorFactory(controlNames: string[], errorMessage: string): ValidatorFn {
    return (control: AbstractControl) => {
      const checkFn = (controlName: string) => control.get(controlName) && control.get(controlName).value;
      if (controlNames.some(checkFn) && !controlNames.every(checkFn)) {
        return {allOrNone: { messageKey: errorMessage }};
      }
      return null;
    };
  }

  // create validator that returns error if first control is not less than or equal to second control if they both have a value
  static lessThanOrEqualValidatorFactory(first: string, second: string, errorMessage: string): ValidatorFn {
    return (control: AbstractControl) => {
      if (control.get(first) && control.get(second)
        && control.get(first).value != null
        && control.get(second).value != null
        && control.get(first).value > control.get(second).value) {
          return {lessThanOrEqual: { messageKey: errorMessage }};
      }
      return null;
    };
  }

  // create validator that performs validation of email username
  static emailUsernameValidator(): ValidatorFn {
    return (control: AbstractControl) => {
      if (control.value != null && !this.EMAIL_USERNAME_REGEX.test(control.value.trim())) {
        return {email: {messageKey: 'Not a valid email username'}};
      }
      return null;
    };
  }

  static validateEmail(email: string): ValidationErrors | null {
    if (email != null && !this.EMAIL_REGEXP.test(email.trim())) {
      return {email: {messageKey: 'Not a valid email address'}};
    }
    return null;
  }

  // create validator that performs validation of single email
  static emailValidator(): ValidatorFn {
    return (control: AbstractControl) => this.validateEmail(control.value);
  }

  // create validator that performs validation of multiple emails
  static multipleEmailValidator(): ValidatorFn {
    return (control: AbstractControl) => {
      const addresses: string[] = control.value ? control.value.split(',') : [];
      for (let i = 0; i < addresses.length; i++) {
        if (!this.EMAIL_REGEXP.test(addresses[i].trim())) {
          return {email: { messageKey: 'Not a valid email address: (address)', parameters: {address: addresses[i]}}};
        }
      }
      return null;
    };
  }

  static requiredIfValidator(predicate: () => boolean): ValidatorFn {
    return (formControl: AbstractControl) => {
      if (predicate()) {
        return Validators.required(formControl);
      }
      return null;
    };
  }

  static number(): ValidatorFn {
    return (control: AbstractControl) => {
      if (control?.value != null) {
        const value = Number(control.value);
        if (value == null || !isFinite(value)) {
          return {invalidNumber: {messageKey: 'Invalid Number'}};
        }
      }

      return null;
    };
  }

  static min(min: number): ValidatorFn {
    const validatorFn = Validators.min(min);
    return (control: AbstractControl) => {
      if (validatorFn(control)) {
        return {min: {messageKey: 'Min Validation Message', parameters: { value: min }}};
      }

      return null;
    };
  }

  static max(max: number): ValidatorFn {
    const validatorFn = Validators.max(max);
    return (control: AbstractControl) => {
      if (validatorFn(control)) {
        return {max: {messageKey: 'Max Validation Message', parameters: { value: max }}};
      }

      return null;
    };
  }
  static positiveNumber: ValidatorFn = (control: AbstractControl) => {
    const value = control.value;
    if (value != null && value <= 0) {
      return {positive: {messageKey: 'Must be a positive number'}};
    }
    return null;
  };
  static positiveInteger: ValidatorFn = Validators.compose([AceValidators.min(1), AceValidators.integer]);
  static safeLongId: ValidatorFn = Validators.compose([AceValidators.positiveInteger, AceValidators.max(AceValidators.INT_64_UPPER_BOUND)]);

  static requiredNotBlank(control: AbstractControl<string>): ValidationErrors | null {
    return control.value?.trim().length ? null : {required: true};
  }

  static integer(control: AbstractControl): ValidationErrors | null {
    return control.value % 1 === 0
      ? null
      : {wholeNumber: {messageKey: 'Integer Validation Message', parameters: {value: control.value}}};
  }

  static customAsyncValidator<T = any>({validationFn, validationWaitMs = 250}: {
    validationFn: (value: T, control?: AbstractControl<T>) => Observable<ValidationErrors | null>,
    validationWaitMs?: number
  }): AsyncValidatorFn {
    return (control: AbstractControl): Observable<ValidationErrors | null> => {
      if (typeof validationFn !== 'function') {
        throw new Error('Request function provided is invalid');
      }
      return of(control).pipe(
        delay(validationWaitMs),
        mergeMap(control => validationFn(control.value, control)));
    };
  }
}
